/*
 * Page de remerciement apres la reservation d'un rendez vous.
 * GoHighLevel redirige ici apres la confirmation du creneau (URL de
 * redirection configuree dans les parametres du calendrier).
 * Aucune donnee n'est lue ni transmise depuis cette page.
 * Regle editoriale : aucun tiret dans les textes visibles.
 */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, FileText, HeartHandshake, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const nextSteps = [
  {
    icon: Mail,
    title: "Ta confirmation arrive par email",
    text: "Tu recevras la confirmation de ton créneau avec la date, l'heure et le lien pour reprogrammer si besoin. Pense à vérifier tes courriers indésirables.",
  },
  {
    icon: FileText,
    title: "Garde ton bilan à portée de main",
    text: "Ton bilan CYCLIQ servira de support à votre échange. Si tu l'as reçu par email, garde ton code personnel avec toi pour l'ouvrir.",
  },
  {
    icon: Sparkles,
    title: "Prépare tes questions",
    text: "Note ce que tu veux comprendre ou clarifier : tes priorités, ton quotidien, tes objectifs. Des questions précises rendent l'échange encore plus utile.",
  },
];

export default function MerciPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Confirmation */}
        <section className="gbs-hero-wash">
          <div className="max-w-[720px] mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#235277] shadow-[var(--shadow-cta)]">
                <CalendarCheck className="w-8 h-8 text-[#EAFE63]" aria-hidden="true" />
              </span>
              <h1
                className="font-heading mt-6 text-3xl sm:text-4xl font-semibold tracking-tight text-[#060606]"
                data-testid="merci-title"
              >
                C'est noté, ton rendez vous est réservé
              </h1>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#333333] max-w-[540px] mx-auto" data-testid="merci-message">
                Merci pour ta confiance. Gino se réjouit d'échanger avec toi pour donner
                du sens à ton bilan et construire la suite, à ton rythme.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Prochaines etapes */}
        <section className="max-w-[720px] mx-auto px-4 sm:px-6 py-10">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#060606] mb-6">
            Et maintenant ?
          </h2>
          <div className="space-y-4" data-testid="merci-next-steps">
            {nextSteps.map((step) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="flex gap-4 items-start rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-5 shadow-[var(--shadow-sm)]"
              >
                <span className="shrink-0 w-10 h-10 rounded-full bg-[#E2F6FF] flex items-center justify-center">
                  <step.icon className="w-5 h-5 text-[#235277]" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-[#060606]">{step.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[#333333]">{step.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Reassurance */}
        <section className="max-w-[720px] mx-auto px-4 sm:px-6 pb-14">
          <div className="relative rounded-2xl bg-[#235277] p-6 sm:p-8 overflow-hidden gbs-grain" data-testid="merci-reassurance">
            <div className="absolute inset-0 gbs-lemon-halo pointer-events-none" aria-hidden="true" />
            <div className="relative flex gap-4 items-start">
              <HeartHandshake className="w-6 h-6 shrink-0 mt-0.5 text-[#EAFE63]" aria-hidden="true" />
              <div>
                <h2 className="font-heading text-lg font-semibold text-white">
                  Un échange sans pression, vraiment
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-[rgba(249,254,255,0.92)]">
                  Cet entretien sert d'abord à comprendre tes résultats et ta situation.
                  Tu restes libre de partager uniquement ce que tu souhaites aborder, et
                  aucune décision ne t'est demandée pendant l'échange. Si un empêchement
                  survient, tu peux reprogrammer ton créneau depuis l'email de confirmation.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/">
              <Button
                data-testid="merci-home-button"
                variant="outline"
                className="min-h-[48px] px-6 rounded-xl border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-heading font-semibold transition-colors duration-200"
              >
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
